/**
 * Category filter chips + inline "Quick Add …" call-to-action for the
 * currently selected category (Veneers / Laminates / Highlighters).
 *
 * Extracted from ProductsPage.js. Pure JSX with
 * external state + setters passed as props — no internal state.
 *
 * The list of categories is intentionally hard-coded here (not fetched
 * from the backend). Keeping it as a static constant means the filter
 * chips are always in a predictable order regardless of which products
 * exist in the DB — HEDA's sales team's preferred layout.
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus } from 'lucide-react';

const CATEGORIES = [
  'Plywood', 'Blockboard', 'Laminates', 'Highlighters', 'Adhesives',
  'Veneers', 'Screws/Patti', 'Hardware', 'Doors', 'MDF', 'WPC/PVC',
];


export function CategoryFilterChips({
  filterCategory,
  onSelectCategory,
  onQuickAddVeneer,
  onQuickAddLaminate,
  onQuickAddHighlighter,
}) {
  return (
    <div
      className="flex gap-1.5 sm:gap-2 mb-3 sm:mb-4 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-hide"
      data-testid="category-filter-chips"
    >
      {CATEGORIES.map(cat => (
        <Button
          key={cat}
          variant={filterCategory === cat ? 'default' : 'outline'}
          size="sm"
          onClick={() => onSelectCategory(cat)}
          className={`rounded-full flex-shrink-0 text-xs sm:text-sm h-8 sm:h-9 px-2.5 sm:px-3 ${filterCategory === cat ? 'bg-heda-ink text-white hover:bg-black' : 'bg-white border-heda-line text-heda-sub hover:bg-white hover:border-heda-gold hover:text-heda-gold'}`}
          data-testid={`category-chip-${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        >
          {cat}
        </Button>
      ))}
      <Button
        variant={!filterCategory ? 'default' : 'outline'}
        size="sm"
        onClick={() => onSelectCategory('')}
        className={`rounded-full flex-shrink-0 text-xs sm:text-sm h-8 sm:h-9 px-2.5 sm:px-3 ${!filterCategory ? 'bg-heda-ink text-white hover:bg-black' : 'bg-white border-heda-line text-heda-sub hover:bg-white hover:border-heda-gold hover:text-heda-gold'}`}
        data-testid="category-chip-all"
      >
        All
      </Button>

      {/* Category-scoped Quick Add CTAs — appear on the right when
          Veneers / Laminates / Highlighters is the active filter.
          Distinct button colours so staff can spot them without
          reading the label. */}
      {filterCategory === 'Veneers' && (
        <Button
          variant="outline"
          size="sm"
          onClick={onQuickAddVeneer}
          className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold ml-auto"
          data-testid="quick-add-veneer-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Quick Add Veneer
        </Button>
      )}
      {filterCategory === 'Laminates' && (
        <Button
          variant="outline"
          size="sm"
          onClick={onQuickAddLaminate}
          className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold ml-auto"
          data-testid="quick-add-laminate-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Quick Add Laminate
        </Button>
      )}
      {filterCategory === 'Highlighters' && (
        <Button
          variant="outline"
          size="sm"
          onClick={onQuickAddHighlighter}
          className="rounded-xl bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold ml-auto"
          data-testid="quick-add-highlighter-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Quick Add Highlighter
        </Button>
      )}
    </div>
  );
}
